import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { downloadParentReport, getAccessFeatureCosts } from "../lib/accessPassApi";
import { toast } from "sonner";
import {
  Users, UserPlus, ArrowLeft, Check, X, Settings, ShieldCheck, Mail, Trash2,
  FileDown, Gem, Loader2,
} from "lucide-react";

/**
 * /dashboard/diploks/parent/children
 * ----------------------------------------------------------------------
 * Parent / guardian → linked-child Diploks management.
 *  - Invite child by email (link starts PENDING; child must accept)
 *  - List linked children with controls (monthly cap, feature allow-list)
 *  - See pending top-up requests + approve / reject
 *  - Revoke link
 */
export default function ParentChildrenPage() {
  const [links, setLinks] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reportLoading, setReportLoading] = useState(null);
  const [featureCosts, setFeatureCosts] = useState({});

  useEffect(() => {
    getAccessFeatureCosts().then(setFeatureCosts).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/diploks/parent/links"),
        api.get("/diploks/parent/topup-requests"),
      ]);
      setLinks(a.data?.links || []);
      setRequests(b.data?.requests || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/diploks/parent/links", { child_email: inviteEmail.trim() });
      toast.success("Invite sent. Your child will need to accept the link.");
      setInviteEmail("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this child link? You will no longer be able to fund or approve their top-ups.")) return;
    try {
      await api.delete(`/diploks/parent/links/${id}`);
      toast.success("Link revoked.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateCap = async (id, monthly_cap_diploks) => {
    try {
      await api.patch(`/diploks/parent/links/${id}`, { monthly_cap_diploks });
      toast.success("Monthly cap updated.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const approveRequest = async (id) => {
    try {
      const r = await api.post(`/diploks/parent/topup-requests/${id}/approve`, {
        accept_terms: true,
        accept_diploks_notice: true,
      });
      if (r.data?.gateway === "stripe" && r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
        return;
      }
      if (r.data?.gateway === "razorpay") {
        const ptId = r.data.payment_transaction_id;
        if (r.data?.mode === "mock") {
          await api.post("/diploks/personal/topup/confirm",
                         { payment_transaction_id: ptId });
          toast.success("Top-up confirmed (mock mode).");
          await load();
          window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
          return;
        }
        await loadRazorpayScript();
        if (!window.Razorpay) {
          toast.error("Razorpay couldn't load. Please refresh and try again.");
          return;
        }
        const rzp = new window.Razorpay({
          key: r.data.razorpay_key_id,
          order_id: r.data.razorpay_order_id,
          amount: r.data.amount_minor,
          currency: "INR",
          name: "Diplomexa",
          description: "Diploks top-up — child wallet",
          handler: async (rzpResp) => {
            await api.post("/diploks/personal/topup/confirm", {
              payment_transaction_id: ptId,
              razorpay_order_id: rzpResp.razorpay_order_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });
            toast.success("Top-up complete. Child's wallet credited.");
            await load();
            window.dispatchEvent(new CustomEvent("diploks:wallet-changed"));
          },
          theme: { color: "#0A192F" },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const rejectRequest = async (id) => {
    const reason = window.prompt("Reason for rejecting (optional):") || "";
    try {
      await api.post(`/diploks/parent/topup-requests/${id}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Top-up request rejected.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="parent-children-page">
        <Link to="/dashboard"
              className="text-xs text-slate-500 hover:text-[#0F4C81] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>

        <div className="mt-3 mb-6">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Parent / Guardian
          </p>
          <h1 className="mt-1 text-3xl font-bold text-[#0A192F] tracking-tight">
            Linked-child Diploks
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Manage Diploks for your child's Diplomexa account. Approve top-up
            requests, set monthly limits, and view their activity.
          </p>
        </div>

        {/* Invite */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6"
             data-testid="parent-invite-panel">
          <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
            Invite a child
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="child@example.com"
              data-testid="parent-invite-email"
              className="flex-1 px-3 py-2 rounded-md border border-slate-200 text-sm"
            />
            <button
              type="button"
              disabled={submitting || !inviteEmail.trim()}
              onClick={sendInvite}
              data-testid="parent-invite-submit"
              className="inline-flex items-center justify-center gap-2 bg-[#0A192F] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[#0F2C50] disabled:opacity-50"
            >
              <UserPlus className="h-3.5 w-3.5" /> Send invite
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            The child will see your request in their Linked Parents section and
            must accept it before any top-ups can be approved.
          </p>
        </div>

        {/* Pending top-up requests — always rendered (testid is queryable
            even when empty). */}
        <div
          className={`rounded-2xl border p-5 mb-6 ${
            requests.length > 0
              ? "border-amber-200 bg-amber-50/50"
              : "border-slate-200 bg-white"
          }`}
          data-testid="parent-pending-requests"
        >
          <p className={`text-xs uppercase tracking-[0.22em] font-bold mb-3 ${
            requests.length > 0 ? "text-amber-700" : "text-slate-500"
          }`}>
            {requests.length > 0
              ? `${requests.length} pending top-up request${requests.length > 1 ? "s" : ""}`
              : "Pending top-up requests"}
          </p>
          {requests.length === 0 ? (
            <p className="text-sm text-slate-500"
               data-testid="parent-pending-requests-empty">
              No pending top-up requests from your linked children.
            </p>
          ) : (
            <div className="space-y-2">
              {requests.map((req) => (
                <div key={req.id}
                     data-testid={`parent-request-${req.id}`}
                     className="flex items-center justify-between rounded-md border border-amber-200 bg-white px-3 py-3">
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">
                      Child {req.child_user_id?.slice(-8) || req.child_user_id}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {req.requested_amount_diploks} Diploks · {req.currency} ·{" "}
                      {req.tier_key}
                      {req.reason ? ` · "${req.reason}"` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => approveRequest(req.id)}
                            data-testid={`parent-approve-${req.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                      <Check className="h-3 w-3" /> Approve
                    </button>
                    <button onClick={() => rejectRequest(req.id)}
                            data-testid={`parent-reject-${req.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-md bg-white border border-slate-200 hover:border-rose-300 text-rose-600">
                      <X className="h-3 w-3" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Linked children */}
        <div className="rounded-2xl border border-slate-200 bg-white"
             data-testid="parent-children-list">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
            <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">
              Linked children
            </p>
          </div>
          {loading ? (
            <p className="px-5 py-4 text-sm text-slate-500">Loading…</p>
          ) : links.length === 0 ? (
            <p className="px-5 py-4 text-sm text-slate-500">
              No linked children yet. Send an invite above to get started.
            </p>
          ) : (
            <div className="divide-y divide-slate-100">
              {links.map((l) => (
                <div key={l.id}
                     data-testid={`parent-child-row-${l.id}`}
                     className="px-5 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-[#0A192F]">
                      {l.child_name || l.child_email}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {l.child_email} ·{" "}
                      <span className={`px-1.5 py-0.5 rounded ${
                        l.status === "active" ? "bg-emerald-50 text-emerald-700"
                          : l.status === "pending" ? "bg-amber-50 text-amber-700"
                          : "bg-slate-100 text-slate-500"}`}>
                        {l.status}
                      </span>
                      {" · "}Monthly cap: {l.monthly_cap_diploks} Diploks
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {l.status === "active" && (
                      <button
                        type="button"
                        disabled={reportLoading === l.child_user_id}
                        onClick={async () => {
                          const cost = featureCosts.access_pass_parent_report?.cost || 150;
                          if (!window.confirm(
                            `Download Premium Parent Event Report for ${l.child_name || l.child_email}?\n\n` +
                            `Premium · Uses Diploks (${cost} Diploks will be charged).`
                          )) return;
                          setReportLoading(l.child_user_id);
                          try {
                            const { blob, diploksCharged } = await downloadParentReport(l.child_user_id);
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `parent-report-${(l.child_name || "child").replace(/\s+/g, "_")}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            URL.revokeObjectURL(url);
                            toast.success(`Report downloaded · ${diploksCharged} Diploks charged.`);
                          } catch (e) {
                            const det = e?.response?.data?.detail;
                            if (typeof det === "object" && det?.code === "insufficient_balance") {
                              toast.error(`Need ${det.cost} Diploks — only ${det.balance_before} available.`);
                            } else if (typeof det === "object" && det?.code === "consent_required") {
                              toast.error("Please accept Diploks Usage Consent in your wallet first.");
                            } else {
                              toast.error(formatApiError(e));
                            }
                          } finally {
                            setReportLoading(null);
                          }
                        }}
                        data-testid={`parent-report-download-${l.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#0A192F] px-2.5 py-1 hover:bg-[#D4AF37]/20 disabled:opacity-50"
                      >
                        {reportLoading === l.child_user_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Gem className="h-3 w-3 text-[#D4AF37]" />
                        )}
                        Premium Report
                      </button>
                    )}
                    {l.status === "active" && (
                      <button onClick={() => {
                        const v = window.prompt(
                          "New monthly cap (in Diploks):",
                          String(l.monthly_cap_diploks),
                        );
                        if (v === null) return;
                        const n = parseInt(v, 10);
                        if (!Number.isFinite(n) || n < 0) {
                          toast.error("Enter a non-negative integer.");
                          return;
                        }
                        updateCap(l.id, n);
                      }}
                              data-testid={`parent-cap-edit-${l.id}`}
                              className="text-slate-400 hover:text-[#0F4C81]">
                        <Settings className="h-4 w-4" />
                      </button>
                    )}
                    <button onClick={() => revoke(l.id)}
                            data-testid={`parent-revoke-${l.id}`}
                            className="text-slate-400 hover:text-rose-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="mt-4 text-[11px] text-slate-500 flex items-start gap-1">
          <ShieldCheck className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
          Top-up requests you approve charge your saved payment method. The
          Diploks are credited to your child's wallet. Diploks are platform
          credits — not cash, not transferable, not withdrawable.
        </p>
      </div>
    </DashboardLayout>
  );
}

let _rzpLoading = null;
function loadRazorpayScript() {
  if (window.Razorpay) return Promise.resolve();
  if (_rzpLoading) return _rzpLoading;
  _rzpLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return _rzpLoading;
}
